import { useEffect, useRef, useState } from 'react'

import { Icon } from '../../components/Brand'
import { getCollectionsUser, number, request, shortDate, titleize } from '../api'
import {
  emptyResetRequest,
  mergePriorityIssues,
  previewCounts,
  priorityErrorsPath,
  priorityIssueView,
  priorityOperationStorageKey,
  readPriorityOperation,
  writePriorityOperation,
  runPriorityUpload,
  resumePriorityUpload,
} from '../imports'
import { Badge, SectionTitle } from './Common'
import PriorityImportedInvoices from './PriorityImportedInvoices'
import PrioritySnapshotReconciliation from './PrioritySnapshotReconciliation'

const CLEARED_FAMILIES = [
  'customers', 'invoices', 'staged imports', 'payments', 'payment reviews',
  'tasks', 'activity', 'escalations', 'reply reviews',
]

function Count({ value, label }) {
  return <span><b>{number(value)}</b>{label}</span>
}

function Groups({ title, values = {} }) {
  const rows = Object.entries(values)
  if (!rows.length) return null
  return <section className="col-card"><SectionTitle title={title} /><div className="col-priority-counts">{rows.map(([key, value]) => <Count key={key} value={value} label={titleize(key)} />)}</div></section>
}

function WorkbookIssue({ issue }) {
  const view = priorityIssueView(issue)
  return <article>
    <header><Badge tone="danger">{titleize(view.code)}</Badge><span>{view.rowNumbers.length ? `Row${view.rowNumbers.length === 1 ? '' : 's'} ${view.rowNumbers.join(', ')}` : 'Workbook issue'}</span></header>
    <h3>{view.message}</h3>
    {(view.field || view.entityReference) && <dl><div><dt>Field</dt><dd>{view.field || '—'}</dd></div><div><dt>Source reference</dt><dd>{view.entityReference || '—'}</dd></div></dl>}
    <p><b>How to fix</b>{view.resolution}</p>
  </article>
}

export default function PriorityUploadIntegration({ resource, onCompleted, onReset, businessUnit = '' }) {
  const inputRef = useRef(null)
  const status = resource.data
  const [staged, setStaged] = useState(null)
  const [working, setWorking] = useState(null)
  const [notice, setNotice] = useState(null)
  const [reconciliation, setReconciliation] = useState(null)
  const [recoverable, setRecoverable] = useState(false)
  const savedOperation = useRef(null)
  const restoredKey = useRef(null)
  const [confirmReset, setConfirmReset] = useState(false)
  const [issues, setIssues] = useState([])
  const [issueNextOffset, setIssueNextOffset] = useState(0)
  const [issueTotal, setIssueTotal] = useState(0)
  const [issueNotice, setIssueNotice] = useState(null)

  const updateStaged = (result) => {
    const inlineIssues = Array.isArray(result?.errors) ? result.errors : []
    const reportedTotal = Number(result?.preview?.error_count ?? inlineIssues.length)
    const total = Number.isFinite(reportedTotal) && reportedTotal >= 0
      ? reportedTotal
      : inlineIssues.length
    setStaged(result)
    setIssues(inlineIssues)
    setIssueNextOffset(inlineIssues.length)
    setIssueTotal(result?.errors_truncated && total <= inlineIssues.length ? inlineIssues.length + 1 : total)
    setIssueNotice(null)
  }

  const clearStaged = () => {
    setStaged(null)
    setIssues([])
    setIssueNextOffset(0)
    setIssueTotal(0)
    setIssueNotice(null)
  }

  const storageKey = priorityOperationStorageKey(`${globalThis.location?.origin || 'local'}:${status.workspace_id || 'local'}`, getCollectionsUser())
  const persist = (operation) => {
    savedOperation.current = operation
    setRecoverable(Boolean(operation))
    writePriorityOperation(globalThis.sessionStorage, storageKey, operation)
  }
  const options = () => ({ requester: request, persist, onRecord: updateStaged, onPhase: setWorking, currentOperation: () => savedOperation.current })
  const showResult = (result) => {
    if (result.state === 'applied') {
      setReconciliation(null)
      setNotice({ error: false, text: 'Priority snapshot is active. Its invoices are listed below.' })
      resource.retry(); onCompleted?.()
    } else if (result.state === 'invalid') {
      setNotice({ error: true, text: 'Correct the workbook issues and upload it again. The active snapshot is unchanged.' })
    } else if (result.state === 'reconciliation') {
      setReconciliation(result.reconciliation)
      setNotice({ error: true, text: 'The uploaded balances need payment coverage review. The previous snapshot remains active.' })
    } else if (result.state === 'historical' || result.state === 'stale') {
      setNotice({ error: true, text: 'The active snapshot changed. Refresh and upload the file again to validate against the current data.' })
      resource.retry()
    } else if (result.state === 'needs_file') {
      setNotice({ error: true, text: 'The upload did not reach the server. Reselect the same file to continue.' })
    } else if (result.state === 'pending') {
      setNotice({ error: false, text: 'The server is recovering this import. Check its status before starting another upload.' })
    }
  }
  const recover = async () => {
    setWorking('recover'); setNotice(null)
    try { showResult(await resumePriorityUpload(savedOperation.current, options())) }
    catch (error) { setNotice({ error: true, text: error.message }) }
    finally { setWorking(null) }
  }
  useEffect(() => {
    if (!storageKey || restoredKey.current === storageKey) return
    restoredKey.current = storageKey
    const operation = readPriorityOperation(globalThis.sessionStorage, storageKey)
    savedOperation.current = operation
    setRecoverable(Boolean(operation))
    if (operation) recover()
  }, [storageKey])

  const upload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setWorking('preview'); setNotice(null); clearStaged(); setReconciliation(null)
    try {
      showResult(await runPriorityUpload(file, status, { ...options(), operation: savedOperation.current }))
    } catch (error) {
      setNotice({ error: true, text: error.message })
      if (error.status === 409) { persist(null); resource.retry() }
    } finally {
      setWorking(null)
      event.target.value = ''
    }
  }

  const reset = async () => {
    setWorking('reset'); setNotice(null)
    try {
      const operation = emptyResetRequest(status.workspace)
      await request('demo/reset', {
        method: 'POST',
        headers: { 'Idempotency-Key': operation.idempotencyKey },
        body: operation.body,
      })
      clearStaged(); setConfirmReset(false); setReconciliation(null); persist(null)
      setNotice({ error: false, text: 'Workspace reset to empty. Source workbooks and configuration were preserved.' })
      resource.retry(); onReset?.()
    } catch (error) {
      setNotice({ error: true, text: error.message })
    } finally { setWorking(null) }
  }

  const loadMoreIssues = async () => {
    const path = priorityErrorsPath(staged)
    if (!path) {
      setIssueNotice('Additional workbook issues are unavailable for this preview.')
      return
    }
    setWorking('errors'); setIssueNotice(null)
    try {
      const page = await request(path, {
        params: { offset: issueNextOffset, limit: 50 },
      })
      const incoming = Array.isArray(page?.items) ? page.items : []
      const pageOffset = Number(page?.offset)
      const consumedOffset = (Number.isInteger(pageOffset) ? pageOffset : issueNextOffset) + incoming.length
      const pageTotal = Number(page?.total)
      if (!incoming.length && Number.isFinite(pageTotal) && consumedOffset < pageTotal) {
        throw new Error('No additional workbook issues were returned. Try again.')
      }
      setIssues((current) => mergePriorityIssues(current, incoming))
      setIssueNextOffset(consumedOffset)
      if (Number.isFinite(pageTotal) && pageTotal >= 0) setIssueTotal(pageTotal)
    } catch (error) {
      setIssueNotice(error.message)
    } finally {
      setWorking(null)
    }
  }

  const snapshot = status.snapshot
  const counts = previewCounts(staged)
  const canApply = staged?.status === 'staged' && counts.importable > 0 && counts.errors === 0
  const needsCorrection = staged?.status === 'invalid' || counts.errors > 0
  const hasMoreIssues = issueNextOffset < issueTotal
  return <div className="col-priority-stack">
    {notice && <div className={notice.error ? 'col-inline-alert' : 'col-inline-success'} role="status"><Icon name={notice.error ? 'error' : 'check_circle'} />{notice.text}</div>}
    <section className="col-card col-priority-hero neutral">
      <div className="col-priority-heading">
        <div className="col-priority-title-row"><Badge tone={snapshot ? 'success' : 'neutral'}>{snapshot ? 'Uploaded snapshot' : 'Empty workspace'}</Badge><span>Priority open items</span></div>
        <h2>{snapshot ? `${number(snapshot.invoice_count)} invoices are active` : 'Upload the first Priority snapshot'}</h2>
        <p>{snapshot ? 'The active customer and invoice records came from the applied workbook below.' : 'Choosing a workbook validates every row and updates the active customer and invoice snapshot when valid.'}</p>
      </div>
      <div className="col-priority-actions">
        <input ref={inputRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={upload} />
        <button className="col-btn" disabled={Boolean(working)} onClick={() => inputRef.current?.click()}><Icon name="upload_file" />{working === 'preview' ? 'Validating…' : working === 'apply' ? 'Applying…' : 'Upload and update invoices'}</button>
        <button className="col-btn secondary" disabled={Boolean(working)} onClick={() => setConfirmReset(true)}><Icon name="delete_sweep" />Reset to empty workspace</button>
      </div>
    </section>

    {snapshot && <section className="col-card"><SectionTitle title="Active snapshot" text="Immutable source and applied version" /><dl className="col-priority-details horizontal"><div><dt>File</dt><dd>{snapshot.filename}</dd></div><div><dt>Applied</dt><dd>{shortDate(snapshot.applied_at)}</dd></div><div><dt>Snapshot version</dt><dd>{snapshot.version}</dd></div><div><dt>Content hash</dt><dd><code>{snapshot.content_hash}</code></dd></div><div><dt>Parser</dt><dd>{snapshot.parser_profile}</dd></div></dl></section>}

    <PriorityImportedInvoices importId={snapshot?.import_id || status.workspace?.active_import_id} businessUnit={businessUnit} />
    {recoverable && !reconciliation && <button className="col-btn secondary" disabled={Boolean(working)} onClick={recover}>Check upload status</button>}
    {reconciliation && <PrioritySnapshotReconciliation conflict={reconciliation} businessUnit={businessUnit} storageKey={storageKey} onResolved={recover} onAccountingChanged={recover} onDiscard={() => { setReconciliation(null); clearStaged(); persist(null); setNotice({ error: false, text: 'The current snapshot was kept. Upload a corrected export when ready.' }) }} />}
    {staged && <>
      <section className="col-card"><SectionTitle title="Uploaded workbook result" text={`${staged.filename} · ${staged.content_hash}`} action={<Badge tone={canApply || staged.status === 'applied' ? 'success' : 'danger'}>{titleize(staged.status)}</Badge>} /><div className="col-priority-preview-counts"><Count value={counts.sourceRows} label="source rows" /><Count value={counts.candidates} label="candidate invoice rows" /><Count value={counts.importable} label="importable invoices" /><Count value={counts.customers} label="customers" /><Count value={counts.companies} label="companies" /><Count value={counts.exclusions} label="classified exclusions" /><Count value={counts.blocked} label="blocked candidate rows" /><Count value={counts.errors} label="blocking issues" /></div><p className="col-muted-copy">Validated for workspace v{staged.expected_workspace_version} · active snapshot v{staged.expected_snapshot_version} · source {staged.preview?.source_sheet} {staged.preview?.source_range}</p>{needsCorrection && <p className="col-priority-invalid-guidance">Correct the workbook and upload it again. No Priority data has changed.</p>}</section>
      {counts.errors > 0 && <section className="col-card col-priority-issues" aria-label="Fix workbook issues"><SectionTitle title="Fix workbook issues" text={`${number(issues.length)} of ${number(issueTotal || counts.errors)} blocking issues shown`} /><div className="col-priority-issue-list">{issues.map((issue, index) => <WorkbookIssue key={`${issue.code || 'issue'}:${issue.row_number || 0}:${issue.field || ''}:${index}`} issue={issue} />)}</div>{issueNotice && <div className="col-inline-alert" role="status"><Icon name="error" />{issueNotice}</div>}{hasMoreIssues && <button className="col-btn secondary" disabled={Boolean(working)} onClick={loadMoreIssues}>{working === 'errors' ? 'Loading issues…' : 'Show more issues'}</button>}</section>}
      <Groups title="Currencies" values={staged.preview?.currency_counts} />
      <Groups title="Exclusions" values={staged.preview?.exclusion_counts} />
      <Groups title="Warnings" values={staged.preview?.warning_counts} />
    </>}

    {confirmReset && <section className="col-card col-priority-confirm danger" role="alertdialog"><div><Icon name="warning" /><span><h2>Reset this demo workspace to empty?</h2><p>This clears {CLEARED_FAMILIES.join(', ')}. Checked-in source workbooks and application configuration are preserved.</p></span></div><footer><button className="col-btn secondary" disabled={working === 'reset'} onClick={() => setConfirmReset(false)}>Cancel</button><button className="col-btn" disabled={working === 'reset'} onClick={reset}>{working === 'reset' ? 'Resetting…' : 'Clear business data'}</button></footer></section>}
  </div>
}
